import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import { PluginKey } from '@tiptap/pm/state';
import { Subject } from 'rxjs';
import { SlashMenuItem } from '../tiptap-editor.types';

export interface SlashSuggestionState {
    visible: boolean;
    items: SlashMenuItem[];
    range: { from: number; to: number } | null;
    clientRect: DOMRect | null;
    query: string;
    selectedIndex: number;
}

export interface SlashMenuOptions {
    /** Character that opens the menu. Defaults to '/'. */
    char?: string;
    /**
     * Whether the query may contain spaces. True (default) for the `/` command menu, where multi-word
     * search is wanted. MENTION menus must set false: a mention is a single token, and with spaces
     * allowed the inserted "@First " keeps matching, so the popup never closes after a pick.
     */
    allowSpaces?: boolean;
    onStateChange: (state: SlashSuggestionState) => void;
    onQueryChange: (query: string) => void;
    getItems: () => SlashMenuItem[];
    onSelect: (item: SlashMenuItem) => void;
}

export const SlashMenuPluginKey = new PluginKey('vfSlashMenu');

/**
 * Builds a suggestion extension bound to ONE trigger character.
 *
 * It has to be a factory rather than a single exported extension because tiptap keys extensions by
 * `name` and ProseMirror keys plugins by `PluginKey` — registering the same extension twice (to get,
 * say, `/` AND `@` in the same editor) silently collides on both. Each call mints its own pair.
 */
export function createSlashMenu(name: string) {
    const pluginKey = new PluginKey(name);
    return Extension.create<SlashMenuOptions>({
    name,

    addOptions() {
        return {
            char: '/',
            allowSpaces: true,
            onStateChange: () => undefined,
            onQueryChange: () => undefined,
            getItems: () => [],
            onSelect: () => undefined
        } as SlashMenuOptions;
    },

    addProseMirrorPlugins() {
        const opts = this.options;
        // Capture the editor so the keyboard handler can dispatch transactions
        // (delete the trigger range) without going through the suggestion
        // plugin's command callback — `props.command` is not part of the
        // public `onKeyDown` props in current @tiptap/suggestion versions.
        const editor = this.editor;
        const stateSubject = new Subject<SlashSuggestionState>();

        const currentState: SlashSuggestionState = {
            visible: false,
            items: [],
            range: null,
            clientRect: null,
            query: '',
            selectedIndex: 0
        };

        const emit = () => {
            opts.onStateChange({ ...currentState });
            stateSubject.next({ ...currentState });
        };

        return [
            Suggestion({
                editor: this.editor,
                pluginKey,
                char: opts.char || '/',
                // Multi-word search: the suggestion's match regex allows
                // spaces inside the query so `/design spec` keeps the menu
                // open. Dismissal rules (immediate space after `/`, two
                // consecutive spaces) live in the `allow` predicate below.
                // Mention menus opt OUT (see allowSpaces on the options).
                allowSpaces: opts.allowSpaces !== false,
                startOfLine: false,
                // A NON-BREAKING space counts as a valid prefix. The default is a plain space only, so
                // typing `/` straight after an inserted reference (which ends in `&nbsp;` for spacing) or
                // after any pasted nbsp never armed the plugin at all — no query, no backend call. It only
                // worked on a fresh line, where start-of-block is allowed unconditionally.
                allowedPrefixes: [' ', String.fromCharCode(160)],
                allow: ({ state, range }) => {
                    // The trigger char occupies position [range.from, range.from + 1),
                    // and the query is the text from `range.from + 1` to `range.to`.
                    const query = state.doc.textBetween(range.from + 1, range.to);
                    // `/<space>` immediately → likely a fraction or typo, dismiss.
                    if (query.length > 0 && query.charAt(0) === ' ') {
                        return false;
                    }
                    // Two consecutive spaces inside the query → user abandoned
                    // the mention, dismiss.
                    if (query.indexOf('  ') >= 0) {
                        return false;
                    }
                    return true;
                },
                command: ({ editor, range, props }) => {
                    editor.chain().focus().deleteRange(range).run();
                    opts.onSelect(props as SlashMenuItem);
                },
                items: ({ query }) => {
                    opts.onQueryChange(query);
                    return opts.getItems();
                },
                render: () => {
                    return {
                        onStart: (props: any) => {
                            currentState.visible = true;
                            currentState.items = props.items;
                            currentState.range = props.range;
                            currentState.clientRect = props.clientRect ? props.clientRect() : null;
                            currentState.query = props.query;
                            currentState.selectedIndex = 0;
                            emit();
                        },
                        onUpdate: (props: any) => {
                            currentState.items = props.items;
                            currentState.range = props.range;
                            currentState.clientRect = props.clientRect ? props.clientRect() : null;
                            currentState.query = props.query;
                            emit();
                        },
                        onKeyDown: (props: any) => {
                            const event: KeyboardEvent = props.event;
                            if (!currentState.visible) {
                                return false;
                            }
                            // Always read the freshest items from the host's
                            // getItems callback. Hosts that load results
                            // asynchronously (mention picker → globalSearch)
                            // update the buffer after the suggestion plugin's
                            // last `onUpdate`, so `currentState.items` is
                            // stale by the time the user hits ArrowDown/Enter.
                            // Re-syncing here keeps keyboard nav working.
                            currentState.items = opts.getItems();
                            // Clamp the selectedIndex in case async results
                            // shrunk the list (otherwise Enter would resolve
                            // to undefined and the pick would silently fail).
                            if (currentState.selectedIndex >= currentState.items.length) {
                                currentState.selectedIndex = 0;
                            }
                            if (event.key === 'Escape') {
                                currentState.visible = false;
                                emit();
                                return true;
                            }
                            if (event.key === 'ArrowDown') {
                                if (currentState.items.length) {
                                    currentState.selectedIndex = (currentState.selectedIndex + 1) % currentState.items.length;
                                    emit();
                                }
                                return true;
                            }
                            if (event.key === 'ArrowUp') {
                                if (currentState.items.length) {
                                    currentState.selectedIndex = (currentState.selectedIndex - 1 + currentState.items.length) % currentState.items.length;
                                    emit();
                                }
                                return true;
                            }
                            if (event.key === 'Enter' || event.key === 'Tab') {
                                const picked = currentState.items[currentState.selectedIndex];
                                if (picked && currentState.range) {
                                    // Replicate the Suggestion plugin's
                                    // configured `command` callback inline:
                                    // delete the trigger char + query range,
                                    // then hand the picked item to the host's
                                    // onSelect (which inserts the chip). We
                                    // don't go through `props.command` because
                                    // it isn't part of the public `onKeyDown`
                                    // props in current @tiptap/suggestion.
                                    editor.chain().focus().deleteRange(currentState.range).run();
                                    opts.onSelect(picked);
                                    currentState.visible = false;
                                    emit();
                                    return true;
                                }
                                return false;
                            }
                            return false;
                        },
                        onExit: () => {
                            currentState.visible = false;
                            emit();
                        }
                    };
                }
            })
        ];
    }
    });
}

/** The default `/` instance — what every editor registered before a second trigger was possible. */
export const SlashMenu = createSlashMenu('vfSlashMenu');

/** Second instance, for editors that want a mention trigger ALONGSIDE `/` (the comment composer). */
export const SecondarySlashMenu = createSlashMenu('vfSlashMenuSecondary');
